// Pure helpers of the guest's room ambience lookup. Relative `.ts` imports only (the ui helpers are install-free too),
// so `node --test` loads them without an install.
import { cinemaTimeZone, DEFAULT_TIME_ZONE, zonedDayKey } from '../../../ui/src/helpers/cinema-time-zone.ts';
import {
    MODEL_TIMEOUT_ERROR_NAME,
    normalizeAmbience,
    type RoomAmbience
} from '../../../ui/src/room-3d/ambience/ambience.ts';
import { dateBounds } from '../../../ui/src/room-3d/ambience/date-window.ts';

/** PostHog boolean flag that switches every guest's ambience off at once, without a deploy. */
export const ROOM_AMBIENCE_KILL_SWITCH_FLAG = 'room-ambience-kill-switch';

/** How long the lookup waits for the flags before it goes ahead: blocked or slow PostHog must not cost the ambience. */
export const KILL_SWITCH_WAIT_MS = 2000;

/**
 * The showing's date at the cinema, `YYYY-MM-DD`. `startDatetime` is an instant (the API serializes the stored UTC
 * value with +00:00), so its own date part is the UTC day: a 00:30 local show would resolve against the day before.
 * The date is read in the cinema's zone (the app's {@link cinemaTimeZone}: its own, its country's, else Berlin).
 */
export function showingDate(
    startDatetime: string | undefined | null,
    timeZone: string | undefined | null
): string | undefined {
    if (!startDatetime) return undefined;
    const instant = new Date(startDatetime);
    if (Number.isNaN(instant.getTime())) return undefined;
    try {
        return zonedDayKey(instant, cinemaTimeZone({ timezone: timeZone }));
    } catch {
        // An unknown zone name: fall back rather than lose the ambience.
        return zonedDayKey(instant, DEFAULT_TIME_ZONE);
    }
}

/**
 * An answer first visible later than this after the lookup was enabled waits for the next mount instead of appearing
 * mid-selection. It is also what makes a slow request harmless: it still warms the cache for the next mount.
 */
export const LATE_AMBIENCE_MS = 4000;

/** Whether the ambience may be shown: `pending` until the flag answers, `killed` switches it off, `clear` lets it through. */
export type KillState = 'pending' | 'killed' | 'clear';

export type KillEvent = { type: 'timeout' } | { type: 'flags'; isKilled: boolean };

/** Where the kill switch starts: no analytics means no PostHog and nothing to wait for; else the page session's last answer. */
export function initialKillState(
    isAnalyticsEnabled: boolean,
    decided: Exclude<KillState, 'pending'> | null
): KillState {
    return isAnalyticsEnabled ? (decided ?? 'pending') : 'clear';
}

/** The flag's answer always wins (a switch thrown or lifted mid-visit lands); the wait timeout only ends `pending`. */
export function reduceKillState(state: KillState, event: KillEvent): KillState {
    if (event.type === 'flags') return event.isKilled ? 'killed' : 'clear';
    return state === 'pending' ? 'clear' : state;
}

/** What a kill-switch state means for the next mount of the seat picker: `pending` has not been decided yet. */
export function decidedKillState(state: KillState): Exclude<KillState, 'pending'> | null {
    return state === 'pending' ? null : state;
}

/**
 * Whether the resolve request may go out: every caller gate open, the flag decided, a cinema known and a date the
 * server accepts (it answers 422 outside today +-2 years). Without a date only a caller that lets the server default
 * it to today may ask.
 */
export function isLookupOpen({
    isEnabled,
    killState,
    cinemaId,
    date,
    isDateOptional,
    today
}: {
    isEnabled: boolean;
    killState: KillState;
    cinemaId: number | undefined;
    date: string | undefined;
    isDateOptional: boolean;
    today: string;
}): boolean {
    if (!isEnabled || killState !== 'clear' || cinemaId === undefined) return false;
    if (!date) return isDateOptional;
    const bounds = dateBounds(today);
    return date >= bounds.min && date <= bounds.max;
}

export type ResolvedAmbience = { ambience: RoomAmbience; sourceSlugs: string[] };

/**
 * Maps the server's answer for rendering. `resolved` is null when nothing matched or nothing usable survived the
 * normalizer; `warnings` are what the normalizer dropped. Sources keep the server's order (film first).
 */
export function toResolvedAmbience(
    response: { ambience: unknown; sources: { id: number; slug: string }[] },
    modelOrigins: string[]
): { resolved: ResolvedAmbience | null; warnings: string[]; sourceSlugs: string[]; sourceIds: number[] } {
    const { ambience, warnings } = normalizeAmbience(response.ambience, { modelOrigins });
    const sourceSlugs = response.sources.map((source) => source.slug);
    const sourceIds = response.sources.map((source) => source.id);
    return { resolved: ambience ? { ambience, sourceSlugs } : null, warnings, sourceSlugs, sourceIds };
}

export type AmbienceFailureReason = 'model_timeout' | 'error';

/** Network weather (a slow model) versus a defect: rollback decisions read the latter only. */
export function ambienceFailureReason(error: unknown): AmbienceFailureReason {
    return error instanceof Error && error.name === MODEL_TIMEOUT_ERROR_NAME ? 'model_timeout' : 'error';
}

/**
 * True when an answer first became visible too late to show in this mount: more than {@link LATE_AMBIENCE_MS} after
 * the lookup was enabled (3D chosen), or after the guest's first seat pick in that view. Judged by first sight, never by a refetch's timestamp, so
 * an ambience already showing is never taken away. `firstSeenAt` null (nothing shown yet) is not late.
 */
export function isAmbienceLate({
    firstSeenAt,
    enabledAt,
    interactedAt
}: {
    firstSeenAt: number | null;
    enabledAt: number;
    interactedAt: number | null;
}): boolean {
    if (firstSeenAt === null) return false;
    return firstSeenAt > enabledAt + LATE_AMBIENCE_MS || (interactedAt !== null && firstSeenAt > interactedAt);
}

/** Share of page sessions whose ambience failures also go to Sentry; PostHog's `seat_map_ambience_failed` counts every one, with a `reason` that separates slow models from defects. */
export const AMBIENCE_REPORT_SAMPLE = 0.05;

/**
 * A gate decided once, on first use: one defect hitting every guest would otherwise cost a Sentry event per booking
 * visit and crowd out checkout errors. A session reports consistently or not at all.
 */
export function createSampleGate(rate: number, random: () => number = Math.random): () => boolean {
    let decided: boolean | null = null;
    return () => (decided ??= random() < rate);
}
